import { toSafeObject } from '../utils/toSafeObject.js';
import { toSafeString } from '../utils/toSafeString.js';
import resolvePieveWorkflowStepIdsInText from './resolveWorkflowStepIdsInText.js';

/*
 * Turns a run failure into the one sentence a person who did not build the workflow can act on.
 *
 * Shared because both sides of the API boundary present the same failure: the run-failure email the
 * client reads at 3am, and the run-logs banner. Two copies would be two different accounts of the
 * same run, and the reader has no way to tell which one is lying.
 *
 * The map is a whitelist on purpose. Codes reaching this shape are not a closed set — the runner
 * mints its own, and passes through whatever the MCP layer, the code evaluator, or a thrown error
 * carried — so a code the whitelist does not know falls back to its failure reason, and an unknown
 * reason falls back to one generic sentence. Prettifying the code string instead (underscores to
 * spaces, lowercased) would read as an explanation while still emitting `AXIOS_ERR_BAD_RESPONSE` at
 * a client, which is the defect this exists to fix.
 *
 * The raw code and raw message are returned untouched alongside the headline. They are the record of
 * what actually happened and every caller keeps showing them — demoted to a details line, never
 * deleted.
 */

const buildSubject = (stepName) => {
  return stepName ? `The "${stepName}" step` : 'A step in this workflow';
};

/* Authoring faults: the workflow cannot succeed on a retry, so the sentence says so rather than
   inviting the reader to wait for the next run. */
const NEEDS_FIX = 'This workflow needs a fix before it can run again.';

const HEADLINE_BY_CODE = {
  BRANCH_INPUT_SELECTOR_INVALID: (subject) => `${subject} could not read the value it decides on, so the run had no path to take.`,
  ROUTER_INPUT_SELECTOR_INVALID: (subject) => `${subject} could not read the value it decides on, so the run had no path to take.`,
  BRANCH_SELECTOR_EXECUTION_FAILED: (subject) => `${subject} could not work out which path to take.`,
  ROUTER_SELECTOR_EXECUTION_FAILED: (subject) => `${subject} could not work out which path to take.`,
  BRANCH_SELECTOR_EMPTY: (subject) => `${subject} did not choose a path to continue on.`,
  ROUTER_SELECTOR_EMPTY: (subject) => `${subject} did not choose a path to continue on.`,
  BRANCH_SELECTOR_KEY_INVALID: (subject) => `${subject} chose a path that no longer exists in this workflow. ${NEEDS_FIX}`,
  ROUTER_SELECTOR_KEY_INVALID: (subject) => `${subject} chose a path that no longer exists in this workflow. ${NEEDS_FIX}`,
  // AI Router (a router whose route is chosen by a decision model). NOT_CONFIGURED and REQUEST_FAILED
  // are ours or the provider's, never the author's, so they do not claim the workflow needs a fix.
  ROUTER_DECISION_STATE_EMPTY: (subject) => `${subject} had nothing to decide on, because the value it reads was empty in this run.`,
  ROUTER_DECISION_STATE_TOO_LARGE: (subject) => `${subject} was given more data than it can decide on at once. ${NEEDS_FIX}`,
  ROUTER_DECISION_NOT_CONFIGURED: (subject) => `${subject} could not reach the service that chooses its path.`,
  ROUTER_DECISION_REQUEST_FAILED: (subject) => `${subject} could not reach the service that chooses its path.`,
  ROUTER_DECISION_KEY_INVALID: (subject) => `${subject} did not get back a path it recognizes.`,
  STEP_TEMPLATE_UNRESOLVED: (subject) => `${subject} needed a value from an earlier step that was not there in this run.`,
  CODE_INPUT_SELECTOR_INVALID: (subject) => `${subject} could not read the data it was given.`,
  CODE_STEP_EXECUTION_FAILED: (subject) => `${subject} stopped with an error while working through the data.`,
  CODE_CONTROL_OUTSIDE_LOOP: (subject) => `${subject} tried to control a loop it is not inside. ${NEEDS_FIX}`,
  AI_STEP_PROMPT_EMPTY: (subject) => `${subject} has no instructions to follow. ${NEEDS_FIX}`,
  AI_STEP_EXECUTION_FAILED: (subject) => `${subject} could not finish its task.`,
  WAIT_DURATION_REQUIRED: (subject) => `${subject} has no wait time set. ${NEEDS_FIX}`,
  WAIT_DURATION_EXCEEDS_MAX: (subject) => `${subject} is set to wait for longer than a single run is allowed to last. ${NEEDS_FIX}`,
  FLOW_STEP_END_RUN_FAILURE: () => 'This workflow stopped itself and reported the run as failed.',
  MCP_ACTION_EXECUTE_FAILED: (subject) => `${subject} could not complete its action in the connected app.`,
  MCP_EXECUTE_FAILED: (subject) => `${subject} could not complete its action in the connected app.`,
  MCP_ACTION_EXECUTE_ABORTED: (subject) => `${subject} was stopped part-way through its action in the connected app.`,
  REQUEST_MAPPER_EXCEPTION: (subject) => `${subject} could not prepare its request because one or more inputs were invalid. ${NEEDS_FIX}`,
  API_REQUEST_MAPPER_EXCEPTION: (subject) => `${subject} could not prepare its request because one or more inputs were invalid. ${NEEDS_FIX}`,
  ACTION_REQUEST_MAPPER_EXCEPTION: (subject) => `${subject} could not prepare its request because one or more inputs were invalid. ${NEEDS_FIX}`,
  REFRESH_TOKEN_FAILURE: (subject) => `${subject} could not refresh its connected account. Try the run again; if it keeps failing, reconnect the app in Pieve.`,
  MCP_CONNECTION_REQUIRED: (subject) => `${subject} needs a connected account before it can run. Connect the app in Pieve and run it again.`,
  MCP_CONNECTION_NOT_FOUND: (subject) => `${subject} could not find the connected account it uses. Reconnect the app in Pieve and run it again.`,
  MCP_CONNECTION_INVALID: (subject) => `${subject} was refused by the connected app. Its connection likely expired — reconnect it in Pieve and run it again.`,
  MCP_CONNECTION_DEFAULT_INACCESSIBLE: (subject) => `${subject} would run through this app's default connection, which belongs to another user. Pick one of your own connections for the step and run it again.`,
  MCP_CONNECTION_AMBIGUOUS: (subject) => `${subject} has more than one account it could use and cannot tell which one is meant. ${NEEDS_FIX}`,
  MCP_APP_NOT_FOUND: (subject) => `${subject} uses an app that is no longer available in Pieve. ${NEEDS_FIX}`,
  MCP_APP_HIDDEN: (subject) => `${subject} uses an app that is no longer available in Pieve. ${NEEDS_FIX}`,
  MCP_ACTION_NOT_FOUND: (subject) => `${subject} uses an action that is no longer available in Pieve. ${NEEDS_FIX}`,
  MCP_ACTION_HIDDEN: (subject) => `${subject} uses an action that is no longer available in Pieve. ${NEEDS_FIX}`,
  MCP_APP_VERSION_NOT_FOUND: (subject) => `${subject} uses a version of its app that is no longer available. ${NEEDS_FIX}`,
  APP_VERSION_UNRESOLVABLE: (subject) => `${subject} uses a version of its app that is no longer available. ${NEEDS_FIX}`,
  PIEVE_CAPABILITY_RESOLUTION_FAILED: () => 'This workflow could not start because one of the apps it uses was unavailable.'
};

/* The closed set from PIEVE_WORKFLOW_RUN_FAILURE_REASON that a failed run can actually carry. */
const HEADLINE_BY_REASON = {
  step_error: (subject) => `${subject} failed while the workflow was running.`,
  timeout: () => 'This workflow ran for longer than it is allowed to and was stopped.',
  out_of_memory: () => 'This workflow handled more data at once than a single run allows and was stopped.',
  compile_failed: () => `This workflow could not be prepared to run. ${NEEDS_FIX}`,
  task_limit_exceeded: () => 'Workflow step limit reached. This run did not start because your organization has used all of its workflow steps for the current 30-day window.'
};

const GENERIC_HEADLINE = 'This workflow run did not finish.';

const resolvePieveWorkflowRunFailureDisplay = ({ failure, stepNameByStepId } = {}) => {
  const safeFailure = toSafeObject(failure);

  if (Object.keys(safeFailure).length < 1) {
    return null;
  }

  const nameByStepId = toSafeObject(stepNameByStepId);
  const code = toSafeString(safeFailure.code) || null;
  const reason = toSafeString(safeFailure.failureReason) || null;
  // `meta.failedStepId` is where the runner files it; the persisted run failure hoists it to the top
  // level. Both shapes reach this function, from the banner and the email respectively.
  const failedStepId = toSafeString(safeFailure.failedStepId || safeFailure.meta?.failedStepId) || null;
  const failedStepName = failedStepId ? toSafeString(nameByStepId[failedStepId]) || null : null;
  const subject = buildSubject(failedStepName);
  const buildHeadline = HEADLINE_BY_CODE[code] || HEADLINE_BY_REASON[reason] || null;

  return {
    headline: buildHeadline ? buildHeadline(subject) : GENERIC_HEADLINE,
    code,
    failedStepId,
    failedStepName,
    // What the runner actually said, step ids swapped for step names. Developer-facing: every caller
    // renders it behind or beneath the headline.
    detail: resolvePieveWorkflowStepIdsInText({ text: toSafeString(safeFailure.message) || toSafeString(safeFailure.summary), stepNameByStepId: nameByStepId }) || null
  };
};

export default resolvePieveWorkflowRunFailureDisplay;
